import React, { useEffect, useState, useRef } from 'react';
import { Routes, Route, Navigate, Link as RouterLink, useNavigate } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import './App.css';
import { supabase, isSupabaseConfigured } from './supabaseClient';
import Auth from './Auth';
import BuildInventory from './BuildInventory';
import AccountPage from './AccountPage';
import TransactionsPage from './TransactionsPage';
import Unauthorized from './Unauthorized';
import { startInactivityTimer, clearInactivityTimer } from './inactivityTimer';
import BuildInventoryViewer from './BuildInventoryViewer';

function RoleRoute({ user, role, allowedRoles, children }) {
  if (!user) return <Navigate to="/login" replace />;
  if (!allowedRoles.includes(role)) return <Navigate to="/unauthorized" replace />;
  return children;
}

function App() {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(isSupabaseConfigured);
  const navigate = useNavigate();
  const hasRedirected = useRef(false);

  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return undefined;
    }

    const getSessionAndRole = async () => {
      const userResult = await supabase.auth.getUser();
      const user = userResult?.data?.user ?? null;
      setUser(user);

      if (user) {
        const { data, error } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', user.id)
          .single();

        if (!error) {
          setRole(data.role);

          if (
            !hasRedirected.current &&
            (window.location.pathname === '/' || window.location.pathname === '/login')
          ) {
            if (data.role === 'administrator' || data.role === 'member') {
              navigate('/transactions', { replace: true });
            } else if (data.role === 'pinnacle' || data.role === 'RainBird') {
              navigate('/build-inventory', { replace: true });
            }
            hasRedirected.current = true;
          }

          startInactivityTimer(async () => {
            await supabase.auth.signOut();
            setUser(null);
            setRole(null);
            navigate('/login');
            alert('Logged out due to 60 minutes of inactivity.');
          });
        }
      } else {
        setRole(null);
      }

      setLoading(false);
    };

    getSessionAndRole();

    const authStateChangeResult = supabase.auth.onAuthStateChange((_event, session) => {
      getSessionAndRole();
    });
    const authListener = authStateChangeResult?.data;

    return () => {
      authListener?.subscription?.unsubscribe?.();
      clearInactivityTimer();
    };
  }, [navigate]);

  if (!isSupabaseConfigured) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          bgcolor: 'background.default',
          p: 3,
        }}
      >
        <Container maxWidth="sm">
          <Paper sx={{ p: { xs: 3, sm: 4 } }}>
            <Typography variant="h5" component="h1" gutterBottom fontWeight={700}>
              Supabase configuration required
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Set these environment variables so the app can connect to your project:
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 2, typography: 'body2', color: 'text.secondary' }}>
              <li>
                <Box component="code" sx={{ bgcolor: 'action.hover', px: 0.75, py: 0.25, borderRadius: 1 }}>
                  REACT_APP_SUPABASE_URL
                </Box>
              </li>
              <li>
                <Box component="code" sx={{ bgcolor: 'action.hover', px: 0.75, py: 0.25, borderRadius: 1 }}>
                  REACT_APP_SUPABASE_ANON_KEY
                </Box>
              </li>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              <strong>Local:</strong> add a <code>.env.local</code> file in the project root (see <code>.env.example</code>),
              then restart <code>npm start</code>.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Vercel:</strong> Project → Settings → Environment Variables — add the same names for Production
              (and Preview if needed), then redeploy.
            </Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 2,
          bgcolor: 'background.default',
        }}
      >
        <CircularProgress color="secondary" />
        <Typography variant="body2" color="text.secondary">
          Loading…
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
      {!loading && (
        <AppBar position="sticky" color="inherit" elevation={0} sx={{ bgcolor: 'background.paper' }}>
          <Toolbar
            sx={{
              maxWidth: 1600,
              width: '100%',
              mx: 'auto',
              px: { xs: 2, sm: 3 },
              py: 1.5,
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Typography
              component={RouterLink}
              to="/"
              variant="subtitle1"
              sx={{
                fontWeight: 800,
                color: 'text.primary',
                textDecoration: 'none',
                mr: 'auto',
                letterSpacing: '-0.03em',
              }}
            >
              Device Build
            </Typography>
            <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" alignItems="center">
              {(role === 'administrator' || role === 'member') && (
                <Button component={RouterLink} to="/transactions" color="inherit">Transactions</Button>
              )}
              {(role === 'administrator' || role === 'member') && (
                <Button component={RouterLink} to="/account" color="inherit">Account</Button>
              )}
              {['administrator', 'pinnacle', 'RainBird'].includes(role) && (
                <Button component={RouterLink} to="/build-inventory" color="inherit">Build Inventory</Button>
              )}
              {user ? (
                <Button
                  color="inherit"
                  onClick={async () => {
                    if (!supabase) return;
                    await supabase.auth.signOut();
                    setUser(null);
                    setRole(null);
                    hasRedirected.current = false;
                    navigate('/login');
                  }}
                >
                  Logout
                </Button>
              ) : (
                <Button component={RouterLink} to="/login" variant="contained" color="secondary">
                  Login
                </Button>
              )}
            </Stack>
          </Toolbar>
        </AppBar>
      )}

      <Container component="main" maxWidth="xl" sx={{ py: { xs: 2, sm: 3 }, px: { xs: 2, sm: 3 }, flex: 1 }}>
        <Routes>
          <Route path="/login" element={<Auth />} />
          <Route path="/unauthorized" element={<Unauthorized />} />
          <Route path="/transactions" element={
            <RoleRoute user={user} role={role} allowedRoles={['administrator', 'member']}>
              <TransactionsPage />
            </RoleRoute>
          } />
          <Route path="/account" element={
            <RoleRoute user={user} role={role} allowedRoles={['administrator', 'member']}>
              <AccountPage />
            </RoleRoute>
          } />
          <Route path="/build-inventory" element={
            user && role ? (
              role === 'RainBird' ? (
                <BuildInventoryViewer />
              ) : (
                <RoleRoute user={user} role={role} allowedRoles={['administrator', 'pinnacle']}>
                  <BuildInventory />
                </RoleRoute>
              )
            ) : (
              <Navigate to="/login" replace />
            )
          } />
          <Route path="/" element={
            user && role ? (
              <Navigate
                to={
                  role === 'administrator' || role === 'member'
                    ? '/transactions'
                    : role === 'pinnacle' || role === 'RainBird'
                    ? '/build-inventory'
                    : '/unauthorized'
                }
                replace
              />
            ) : (
              <Navigate to="/login" replace />
            )
          } />
        </Routes>
      </Container>
    </Box>
  );
}

export default App;
